import AppsIcon from '@mui/icons-material/Apps'
import { Box, ClickAwayListener, IconButton, Popover, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useRef, useState } from 'react'

import { DragDropContext, DropResult } from '@hello-pangea/dnd'
import AddRoadIcon from '@mui/icons-material/AddRoad'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GridViewIcon from '@mui/icons-material/GridView'
import GroupsIcon from '@mui/icons-material/Groups'
import InsightsIcon from '@mui/icons-material/Insights'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction'
import PersonIcon from '@mui/icons-material/Person'
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges'
import ReceiptIcon from '@mui/icons-material/Receipt'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import { useNavigate } from 'react-router-dom'
import './boardsNav.css'
import BoardsNavDraggable from './BoardsNavDraggable'
const BoardNavButton = () => {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false)
  const theme = useTheme()
  const { isMobile, isTablet, isDesktop } = useLayoutContext()

  const _iconRefs = useRef([])
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
    setIsMenuOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setIsMenuOpen(false)
  }
  const LOCAL_STORAGE_KEY = 'boardOrder'

  const defaultBoards = [
    {
      title: 'Teams',
      route: '/teams',
      color: (theme.palette.icon as any).teams,
      icon: <GroupsIcon />,
      disabled: false,
    },
    {
      title: 'Products',
      route: '/products',
      color: theme.palette.icon.products,
      icon: <LocalOfferIcon />,
      disabled: false,
    },
    {
      title: 'Approvals',
      route: '/approvals',
      color: (theme.palette.icon as any).approvals,
      icon: <PublishedWithChangesIcon />,
      disabled: false,
    },
    {
      title: 'Accounting',
      route: '/accounting',
      color: (theme.palette.icon as any).accounting,
      icon: <ReceiptIcon />,
      disabled: false,
    },
    {
      title: 'Contacts',
      route: '/contacts',
      color: theme.palette.icon.contacts,
      icon: <PersonIcon />,
      disabled: false,
    },
    {
      title: 'Projects',
      route: '/projects',
      color: (theme.palette.icon as any).projects,
      icon: <RocketLaunchIcon />,
      disabled: false,
    },
    {
      title: 'Dashboard',
      route: '/dashboard',
      color: (theme.palette.icon as any).dashboard,
      icon: <GridViewIcon />,
      disabled: false,
    },
    {
      title: 'Deals',
      route: '/deals',
      color: 'rgb(251, 193, 30)',
      icon: <AttachMoneyIcon />,
      disabled: false,
    },
    {
      title: 'Leads',
      route: '/leads',
      color: 'rgb(94, 123, 167)',
      icon: <OnlinePredictionIcon />,
      disabled: false,
    },
    {
      title: isMobile || isTablet ? 'MTB' : 'Monthly Task Board',
      route: '/mtb',
      color: 'rgb(20, 119, 248)',
      icon: <CalendarMonthIcon />,
      disabled: false,
    },
    {
      title: isMobile || isTablet ? 'NCO' : 'New Client Onboards',
      route: '/nco',
      color: 'rgb(52, 211, 145)',
      icon: <AddRoadIcon />,
      disabled: false,
    },
    {
      title: 'Organizations',
      route: '/organizations',
      color: 'rgb(114, 229, 218)',
      icon: <CorporateFareIcon />,
      disabled: false,
    },
    {
      title: 'Team Tasks',
      route: '/teamtasks',
      color: 'rgb(247, 63, 236)',
      icon: <TaskAltIcon />,
      disabled: false,
    },
    {
      title: 'Metrics',
      route: '/metrics',
      color: 'rgb(84, 107, 240)',
      icon: <InsightsIcon />,
      disabled: false,
    },
    {
      title: 'Reports',
      route: '/reports',
      color: 'rgb(105, 107, 255)',
      icon: <DashboardIcon />,
      disabled: false,
    },
  ]

  const getInitialBoards = () => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (saved) {
      const savedOrder = JSON.parse(saved)
      // Preserve icon/functionality by mapping order
      return savedOrder.map((route: string) => defaultBoards.find(b => b.route === route)).filter(Boolean)
    }
    return defaultBoards
  }
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return

    const reordered = Array.from(boards)
    const [removed] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, removed)

    setBoards(reordered)
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reordered.map((b: any) => b.route)))
  }

  const [boards, setBoards] = useState(() => getInitialBoards())

  const open = Boolean(anchorEl)

  const handleNavigate = (route: string) => {
    const boardViewMapping: Record<string, string> = {
      '/teams': 'teams',
      '/nco': 'nco',
      '/projects': 'projects',
      '/website': 'website',
      '/mtb': 'mtb',
      '/products': 'products',
      '/deals': 'deals',
      '/organizations': 'organizations',
      '/contacts': 'contacts',
      '/leads': 'leads',
      '/teamtasks': 'teamtasks',
      '/metrics': 'metrics',
      '/dashboard': 'dashboard',
    }

    const newView = boardViewMapping[route]
    if (newView) {
      localStorage.setItem('boardSelectView', JSON.stringify(newView))
    }

    handleClose()
    navigate(route)
  }
  const handleResetOrder = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    setBoards(defaultBoards)
  }

  return (
    <Box>
      <Tooltip title="Boards">
        <IconButton
          onClick={handleClick}
          sx={{
            p: 0,
            borderRadius: 0,
            minWidth: 32,
            minHeight: 32,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'transform 0.2s ease-in-out',
            '& .MuiSvgIcon-root': {
              color: 'text.primary',
              transition: 'color 0.2s ease-in-out',
            },
            '&:hover': {
              backgroundColor: 'transparent',
              transform: 'scale(1.2)',
              '& .MuiSvgIcon-root': {
                color: 'text.highlight',
              },
            },
          }}
        >
          <AppsIcon sx={{ fontSize: 30, color: isMenuOpen ? 'text.highlight' : 'text.primary' }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          zIndex: 1401,
          '& .MuiPopover-paper': {
            width: '100%',
            maxWidth: '23vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: 1,
            borderRadius: '8px',
            border: `1px solid ${theme.palette.text.primary}`,
            boxShadow: '0px 4px 10px rgba(0,0,0,0.15)',
          },
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Stack
            direction="column"
            sx={{
              position: 'relative',
              height: '100%',
              maxHeight: '75vh',
            }}
          >
            {/* Scrollable list container */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                // pr: 1,
              }}
            >
              <DragDropContext onDragEnd={handleDragEnd}>
                <BoardsNavDraggable boards={boards} onNavigate={handleNavigate} />
              </DragDropContext>
            </Box>

            {/* Fixed footer */}
            <Box
              onClick={handleResetOrder}
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                bottom: 0,
                left: 0,
                width: '100%',
                bgcolor: theme.palette.background.paper,
                py: 1,
                px: 2,
                textAlign: 'center',
                fontWeight: 500,
                fontSize: 14,
                color: theme.palette.primary.main,
                cursor: 'pointer',
                zIndex: 1,
                // py: 1,
                // px: 2,
                // textAlign: 'center',
                // fontWeight: 500,
                // fontSize: 14,
                // color: theme.palette.primary.main,
                // cursor: 'pointer',
                '&:hover': {
                  color: 'text.primary',
                  // backgroundColor: theme.palette.text.secondary,
                },
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Typography>Reset to Default Order</Typography>
            </Box>
          </Stack>
        </ClickAwayListener>
      </Popover>
    </Box>
  )
}

export default BoardNavButton
